import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  TextField
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Person as TrainerIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { formatDateTime } from '../utils/dateUtils';
import PaymentReceiptDialog from './PaymentReceiptDialog';
import api from '../utils/api';
import { isWireTransferEnabled } from '../utils/featureFlags';
import { useTranslation } from 'react-i18next';

const ClassBookingDialog = ({ open, onClose, classData, onSuccess }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('details'); // 'details', 'payment', 'processing', 'success'
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit_card');
  const [completedPayment, setCompletedPayment] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Create payment options for the payment processor hook
  const paymentOptions = pricingData ? {
    registration: {
      amount: pricingData.pricing.calculatedPrice,
      selectedPlan: {
        name: `${pricingData.className} Class`
      }
    }
  } : null;

  // For now, let's create a simpler payment flow specific to class bookings
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [creditCardForm, setCreditCardForm] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardHolderName: ''
  });

  const handleMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    
    if (method === 'credit_card') {
      setPaymentDialogOpen(true);
    } else if (method === 'wire_transfer') {
      setCompletedPayment({ status: 'pending', method: method });
      setStep('success');
      // Trigger refresh of class data to show user is now booked
      if (onSuccess) {
        onSuccess();
      }
    } else if (method === 'in_place') {
      setCompletedPayment({ status: 'pending', method: method });
      setStep('success');
      // Trigger refresh of class data to show user is now booked
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const handleCreditCardPayment = async () => {
    try {
      setLoading(true);
      setError('');

      // Use the existing class payment completion endpoint with the payment data
      const response = await api.post('/scheduled-classes/complete-payment-booking', {
        paymentId: paymentData.id,
        cardDetails: creditCardForm
      });

      if (response.data.success) {
        setCompletedPayment({ status: 'completed' });
        setPaymentDialogOpen(false);
        setStep('success');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError('Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('Credit card payment error:', err);
      setError(err.response?.data?.error || 'Payment processing failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/scheduled-classes/${classData._id}/pricing`);

      if (response.data.success) {
        setPricingData(response.data.data);
      } else {
        setError(response.data.error || 'Failed to get pricing information');
      }
    } catch (err) {
      console.error('Error fetching pricing data:', err);
      setError(err.response?.data?.error || 'Failed to get pricing information');
    } finally {
      setLoading(false);
    }
  }, [classData]);

  // Fetch pricing data when dialog opens
  useEffect(() => {
    if (open && classData) {
      fetchPricingData();
    }
  }, [open, classData, fetchPricingData]);

  const handleBookClass = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post(`/scheduled-classes/${classData._id}/book-with-payment`, {
        paymentMethod: selectedPaymentMethod
      });

      if (response.data.success) {
        const { data } = response.data;
        
        // If class is free, booking is complete
        if (data.booking?.type === 'free') {
          setCompletedPayment({ status: 'free' });
          setStep('success');
          return;
        }

        // If payment is required, store payment data and handle payment method
        if (data.payment) {
          setPaymentData(data.payment);
          handleMethodSelect(selectedPaymentMethod);
        }
      } else {
        setError(response.data.error || 'Failed to create booking');
      }
    } catch (err) {
      console.error('Error booking class:', err);
      setError(err.response?.data?.error || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    if (step === 'success' && onSuccess) {
      onSuccess();
    }
    
    setStep('details');
    setPricingData(null);
    setError('');
    setCompletedPayment(null);
    setShowReceipt(false);
    onClose();
  };

  const renderClassDetails = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {pricingData?.className}
            </Typography>
            {pricingData?.pricing.isFree ? (
              <Chip label={t('booking.includedInPlan', { ns: 'booking' })} color="success" />
            ) : (
              <Chip label={pricingData?.pricing.formattedPrice} color="primary" />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TimeIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2">
              {formatDateTime(pricingData?.startTime)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TrainerIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2">
              {pricingData?.trainer.firstName} {pricingData?.trainer.lastName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LocationIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2">
              {pricingData?.facility.name}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Available Spots:
            </Typography>
            <Typography variant="body2">
              {pricingData?.availability.availableSpots} / {pricingData?.availability.maxParticipants}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Your Plan:
            </Typography>
            <Typography variant="body2">
              {pricingData?.userPlan.name}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pricing:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {pricingData?.pricing.formattedPrice || `₺${pricingData?.pricing.calculatedPrice || 0}`}
            </Typography>
          </Box>

          {pricingData?.availability.requiresApproval && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This booking requires approval from the administrator.
            </Alert>
          )}
        </CardContent>
      </Card>

      {pricingData?.pricing && (
        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {pricingData.pricing.isFree ? t('booking:paymentMethod.titleOptional') : t('booking:paymentMethod.title')}
            </FormLabel>
            <RadioGroup
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            >
              <FormControlLabel
                value="credit_card"
                control={<Radio />}
                label={t('booking.paymentMethods.creditCard', { ns: 'booking' })}
              />
              {isWireTransferEnabled() && (
                <FormControlLabel
                  value="wire_transfer"
                  control={<Radio />}
                  label={t('booking.paymentMethods.wireTransfer', { ns: 'booking' })}
                />
              )}
              <FormControlLabel
                value="in_place"
                control={<Radio />}
                label={t('booking.paymentMethods.payAtGym', { ns: 'booking' })}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  const renderPaymentStep = () => (
    <Box sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <PaymentIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>
          Payment Required
        </Typography>
      </Box>
      
      {selectedPaymentMethod === 'credit_card' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Credit card payment integration is not yet available. Please choose wire transfer or pay at gym reception.
        </Alert>
      )}
      
      {isWireTransferEnabled() && selectedPaymentMethod === 'wire_transfer' && completedPayment && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Your booking has been created with pending payment status. Please complete the wire transfer within 3 days using the instructions below.
          </Alert>
          <Typography variant="h6" gutterBottom>Wire Transfer Instructions</Typography>
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2"><strong>Amount:</strong> ₺{completedPayment.amount}</Typography>
            <Typography variant="body2"><strong>Reference:</strong> {completedPayment.token}</Typography>
            <Typography variant="body2"><strong>Instructions will be sent to your email shortly.</strong></Typography>
          </Box>
        </Box>
      )}
      
      {selectedPaymentMethod === 'in_place' && completedPayment && (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your booking has been created! Please visit gym reception to complete payment.
          </Alert>
          <Typography variant="h6" gutterBottom>Payment Token</Typography>
          <Box sx={{ p: 3, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
              {completedPayment.token}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Show this token at gym reception
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Amount to pay:</strong> ₺{completedPayment.amount}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Your booking will be confirmed once payment is completed at reception.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderProcessingStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={60} sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>
        Confirming Booking
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we confirm your class booking...
      </Typography>
    </Box>
  );

  const renderSuccessStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>
        {pricingData?.pricing.isFree || completedPayment?.status === 'free' ? t('booking:bookingStatus.confirmed') :
         completedPayment?.status === 'completed' ? t('booking:bookingStatus.confirmed') : t('booking:bookingStatus.created')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {pricingData?.pricing.isFree || completedPayment?.status === 'free'
          ? t('booking:bookingStatus.freeBookingMessage')
          : completedPayment?.status === 'completed'
            ? t('booking:bookingStatus.paidBookingMessage')
            : isWireTransferEnabled() && completedPayment?.method === 'wire_transfer'
              ? t('booking:bookingStatus.wireTransferMessage')
              : completedPayment?.method === 'in_place'
                ? t('booking:bookingStatus.inPlaceMessage')
                : t('booking:bookingStatus.pendingMessage')
        }
      </Typography>
      
      {completedPayment && completedPayment.status === 'completed' && (
        <Button
          variant="outlined"
          startIcon={<ReceiptIcon />}
          onClick={() => setShowReceipt(true)}
          sx={{ mt: 2 }}
        >
          View Receipt
        </Button>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 }
      }}
    >
      <DialogTitle>
        {step === 'details' && t('booking:buttons.bookClass')}
        {step === 'payment' && t('booking:dialogTitles.paymentInstructions')}
        {step === 'processing' && t('booking:dialogTitles.processing')}
        {step === 'success' && (pricingData?.pricing.isFree ? t('booking:dialogTitles.bookingConfirmed') : t('booking:dialogTitles.bookingCreated'))}
      </DialogTitle>

      <DialogContent>
        {loading && step === 'details' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && pricingData && step === 'details' && renderClassDetails()}
        {step === 'payment' && renderPaymentStep()}
        {step === 'processing' && renderProcessingStep()}
        {step === 'success' && renderSuccessStep()}
      </DialogContent>

      <DialogActions>
        {step === 'details' && (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBookClass}
              disabled={loading || !pricingData}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {pricingData?.pricing.isFree ? t('booking:buttons.bookClass') : `${t('booking:buttons.createBooking')} (${pricingData?.pricing.formattedPrice})`}
            </Button>
          </>
        )}

        {step === 'success' && (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}

        {step === 'payment' && (
          <>
            <Button onClick={() => setStep('details')}>
              Back
            </Button>
            <Button variant="contained" onClick={() => setStep('success')}>
              Continue
            </Button>
          </>
        )}

        {step === 'processing' && (
          <Button onClick={handleClose}>
            Cancel
          </Button>
        )}
      </DialogActions>

      {/* Payment Receipt Dialog */}
      <PaymentReceiptDialog
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
        paymentData={completedPayment}
        classData={pricingData}
      />

      {/* Credit Card Payment Dialog */}
      <Dialog open={paymentDialogOpen && selectedPaymentMethod === 'credit_card'} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Credit Card Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Please enter your credit card details to complete the class booking payment.
            </Alert>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('booking.cardNumber', { ns: 'booking' })}
                  value={creditCardForm.cardNumber}
                  onChange={(e) => setCreditCardForm(prev => ({ ...prev, cardNumber: e.target.value.replace(/\D/g, '').slice(0, 16) }))}
                  placeholder="1234567812345678"
                  required
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('booking.cardHolderName', { ns: 'booking' })}
                  value={creditCardForm.cardHolderName}
                  onChange={(e) => setCreditCardForm(prev => ({ ...prev, cardHolderName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="MM/YY"
                  value={creditCardForm.expiryMonth && creditCardForm.expiryYear ? `${creditCardForm.expiryMonth.toString().padStart(2, '0')}/${creditCardForm.expiryYear.toString().slice(-2)}` : ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    const month = value.slice(0, 2);
                    const year = value.slice(2, 4);
                    setCreditCardForm(prev => ({
                      ...prev,
                      expiryMonth: month ? parseInt(month) : '',
                      expiryYear: year ? parseInt('20' + year) : ''
                    }));
                  }}
                  placeholder="MM/YY"
                  inputProps={{ maxLength: 5 }}
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="CVV"
                  value={creditCardForm.cvv}
                  onChange={(e) => setCreditCardForm(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  placeholder="123"
                  inputProps={{ maxLength: 4 }}
                  required
                />
              </Grid>
            </Grid>
            
            {paymentOptions && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Amount to Pay:</strong> ₺{paymentOptions.registration.amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Class:</strong> {paymentOptions.registration.selectedPlan.name}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreditCardPayment}
            variant="contained"
            disabled={loading || !creditCardForm.cardNumber || !creditCardForm.cardHolderName || !creditCardForm.expiryMonth || !creditCardForm.expiryYear || !creditCardForm.cvv}
            startIcon={loading && <CircularProgress size={16} />}
          >
            {loading ? t('booking:dialogTitles.processingPayment') : `Pay ₺${paymentOptions?.registration.amount || '0'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ClassBookingDialog;